/**
 * Bitcoin Cashback Program Section
 * 
 * Clean, professional explanation of how the cashback program works.
 * Trust-first design: light background, brand colors, no aggressive urgency.
 */

import { ArrowRight, Shield, Smartphone, Wallet } from 'lucide-react';
import { usePricing } from '@/contexts/PricingContext';

interface BtcOfferSectionProps {
  onCtaClick: () => void;
}

export function BtcOfferSection({ onCtaClick }: BtcOfferSectionProps) {
  const pricing = usePricing();
  return (
    <section className="py-16 bg-[#f8f7f4]" id="cashback-program">
      <div className="container px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6a63f5]/8 border border-[#6a63f5]/15 text-[#6a63f5] text-sm font-medium mb-6">
            <Wallet className="w-4 h-4" />
            Bitcoin Cashback Program
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
            How the Cashback Program Works
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Instead of spending our marketing budget on ads, we invest it directly in you.
            Purchase Ryder One and receive $200 back in Bitcoin.
          </p>
        </div>

        {/* 3-Step Visual Flow */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {/* Step 1 */}
          <div className="relative group">
            <div className="bg-white border border-border/60 rounded-2xl p-6 text-center hover:border-[#6a63f5]/30 transition-all duration-300 h-full shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#6a63f5]/8 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#6a63f5]" />
              </div>
              <div className="text-[#6a63f5] text-xs font-semibold uppercase tracking-wider mb-2">Step 1</div>
              <h3 className="text-foreground font-bold text-lg mb-2">Purchase Ryder One</h3>
              <p className="text-muted-foreground text-sm">Ryder One wallet + free welcome kit: Recovery Tag, wireless charger, and travel pouch.</p>
              <div className="mt-4 text-2xl font-bold text-foreground">${pricing.salePrice}</div>
            </div>
            {/* Arrow connector (desktop) */}
            <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20">
              <ArrowRight className="w-5 h-5 text-muted-foreground/40" />
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative group">
            <div className="bg-white border border-border/60 rounded-2xl p-6 text-center hover:border-[#6a63f5]/30 transition-all duration-300 h-full shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#6a63f5]/8 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-[#6a63f5]" />
              </div>
              <div className="text-[#6a63f5] text-xs font-semibold uppercase tracking-wider mb-2">Step 2</div>
              <h3 className="text-foreground font-bold text-lg mb-2">Set Up & Activate</h3>
              <p className="text-muted-foreground text-sm">Set up your wallet in 60 seconds and activate it through the Ryder app.</p>
              <div className="mt-4 text-sm text-muted-foreground">Takes under 5 minutes</div>
            </div>
            {/* Arrow connector (desktop) */}
            <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20">
              <ArrowRight className="w-5 h-5 text-muted-foreground/40" />
            </div>
          </div>

          {/* Step 3 */}
          <div className="group">
            <div className="bg-white border border-[#6a63f5]/20 rounded-2xl p-6 text-center hover:border-[#6a63f5]/40 transition-all duration-300 h-full shadow-sm ring-1 ring-[#6a63f5]/10">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#6a63f5]/10 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-[#6a63f5]" />
              </div>
              <div className="text-[#6a63f5] text-xs font-semibold uppercase tracking-wider mb-2">Step 3</div>
              <h3 className="text-foreground font-bold text-lg mb-2">Receive $200 in BTC</h3>
              <p className="text-muted-foreground text-sm">Bitcoin is deposited directly into your Ryder One wallet within 30 days of purchase.</p>
              <div className="mt-4 text-2xl font-bold text-[#6a63f5]">$200 BTC</div>
            </div>
          </div>
        </div>

        {/* Simple math summary */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="bg-white border border-border/60 rounded-2xl p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-center">
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">You pay</div>
                <div className="text-foreground text-xl font-bold">${pricing.salePrice}</div>
              </div>
              <div className="text-muted-foreground/40 text-xl">→</div>
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">You receive back</div>
                <div className="text-[#6a63f5] text-xl font-bold">$200 in BTC</div>
              </div>
              <div className="text-muted-foreground/40 text-xl">→</div>
              <div className="bg-[#6a63f5]/5 border border-[#6a63f5]/15 rounded-xl px-5 py-3">
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Your net cost</div>
                <div className="text-[#6a63f5] text-xl font-bold">Effectively $0</div>
              </div>
            </div>
          </div>
        </div>

        {/* Why section */}
        <div className="max-w-2xl mx-auto text-center mb-10">
          <h3 className="text-foreground font-semibold text-lg mb-3">Why are we doing this?</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Customer acquisition in crypto typically costs $150–300 per user through advertising.
            Instead of giving that money to ad platforms, we redirect it to you as Bitcoin cashback.
            You get a premium hardware wallet and your money back. We get a customer who loves the product.
            It's a straightforward trade.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button 
            onClick={onCtaClick}
            className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full text-base transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
            style={{ 
              backgroundColor: '#6a63f5',
              boxShadow: '0 2px 12px rgba(106, 99, 245, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(106, 99, 245, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(106, 99, 245, 0.3)';
            }}
          >
            {`Get Ryder One — $${pricing.salePrice}`}
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-muted-foreground text-xs mt-3">Cashback program available through February 2026.</p>
        </div>
      </div>
    </section>
  );
}

export default BtcOfferSection;
